import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Store, MapPin, User, Phone } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BUSINESS_TYPES = [
  "Food / Provisions",
  "Fashion / Clothing",
  "Salon / Beauty",
  "POS / Mobile money",
  "Electronics",
  "Restaurant / Bukka",
  "Pharmacy",
  "Hardware / Building",
  "Other",
];

const LANGUAGES = [
  { value: "english", label: "English", flag: "🇬🇧", subtitle: "I speak English" },
  { value: "pidgin", label: "Pidgin", flag: "🇳🇬", subtitle: "I sabi Pidgin" },
  { value: "yoruba", label: "Yorùbá", flag: "🟢", subtitle: "Mo ń sọ Yorùbá" },
  { value: "hausa", label: "Hausa", flag: "🟢", subtitle: "Ina iya Hausa" },
  { value: "igbo", label: "Igbo", flag: "🟢", subtitle: "Ana m asụ Igbo" },
];

export default function Onboard() {
  const [form, setForm] = useState({
    owner_name: "",
    business_name: "",
    business_type: "",
    location: "",
    phone: "",
    preferred_language: "english",
  });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["owner_name", "business_name", "business_type", "location"]) {
      if (!form[k]) { toast.error("Please fill all fields"); return; }
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/onboard", form);
      setUser(r.data.user);
      toast.success("Welcome to Kobolog AI! 🎉");
      nav("/welcome");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-kudi-cream">
      <div className="max-w-md mx-auto px-5 py-8">
        <div className="mb-8">
          <div className="text-xs font-semibold text-kudi-green tracking-widest uppercase">Step 1 of 1</div>
          <h1 className="font-heading text-3xl font-bold mt-2">Tell us about your business</h1>
          <p className="mt-2 text-kudi-ink/65">Helps Kobolog AI talk to you like family.</p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="onboard-form">
          <Field
            label="Your name"
            icon={User}
            placeholder="e.g. Ngozi"
            value={form.owner_name}
            onChange={(v) => setForm({ ...form, owner_name: v })}
            testid="onboard-owner-name"
          />
          <Field
            label="Business name"
            icon={Store}
            placeholder="e.g. Mama Ngozi Provisions"
            value={form.business_name}
            onChange={(v) => setForm({ ...form, business_name: v })}
            testid="onboard-business-name"
          />
          <div>
            <label className="text-sm font-medium text-kudi-ink/80">Business type</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {BUSINESS_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, business_type: t })}
                  className={`text-sm py-3 px-3 rounded-xl border transition ${
                    form.business_type === t
                      ? "bg-kudi-green text-white border-kudi-green"
                      : "bg-white border-border text-kudi-ink hover:border-kudi-green/40"
                  }`}
                  data-testid={`type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <Field
            label="Location"
            icon={MapPin}
            placeholder="e.g. Mile 12, Lagos"
            value={form.location}
            onChange={(v) => setForm({ ...form, location: v })}
            testid="onboard-location"
          />
          <div>
            <Field
              label="WhatsApp number (optional)"
              icon={Phone}
              placeholder="+234 80 1234 5678"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              testid="onboard-phone"
            />
            <p className="text-xs text-kudi-ink/50 mt-1.5">Needed for WhatsApp bookkeeping + Paystack payouts. You can add this later in Profile.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-kudi-ink/80">Preferred language</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => setForm({ ...form, preferred_language: l.value })}
                  className={`text-left p-3 rounded-xl border transition ${
                    form.preferred_language === l.value
                      ? "bg-kudi-green text-white border-kudi-green"
                      : "bg-white border-border text-kudi-ink hover:border-kudi-green/40"
                  }`}
                  data-testid={`lang-${l.value}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{l.flag}</span>
                    <span className="font-heading font-semibold">{l.label}</span>
                  </div>
                  <div className={`text-[10px] mt-1 ${form.preferred_language === l.value ? "text-white/80" : "text-kudi-ink/55"}`}>{l.subtitle}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-kudi-ink/50 mt-2">Kobolog go reply you in this language.</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-kudi-green text-white rounded-full py-4 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-50 active:scale-[0.98] mt-2"
            data-testid="onboard-submit"
          >
            {loading ? "Setting up..." : <>Enter Kobolog AI <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, placeholder, value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-kudi-ink/80">{label}</label>
      <div className="mt-1.5 relative">
        {Icon && <Icon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-kudi-ink/40" />}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-white border border-border rounded-xl ${Icon ? "pl-11" : "pl-4"} pr-4 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-kudi-green/20 focus:border-kudi-green transition`}
          data-testid={testid}
        />
      </div>
    </div>
  );
}
